import React, { useState } from 'react';
import { message, Button, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/store';
import SaveDialog from './SaveDialog';
import ProjectDropdown from './ProjectDropdown';
import { useTranslation } from 'react-i18next';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('Toolbar');
import { useTelemetry } from '../hooks/useTelemetry';
import putkiLogo from '../assets/putki-logo.svg';
import { UserProfile } from './auth/UserProfile';
import { useTheme } from '../hooks/useTheme';

// Type definitions
interface PipelineData {
  $typeName: string;
  workflowPipelineType: number;
  name: string;
  description: string;
  parameters: any[];
  actionTransforms: ActionTransform[];
  notepads: any[];
  hops: Hop[];
  locations: Location[];
}

interface ActionTransform {
  $typeName: string;
  name: string;
  description: string;
  type: string;
}

interface Hop {
  $typeName: string;
  from: string | undefined;
  to: string | undefined;
  enabled: boolean;
  hopType: number;
}

interface Location {
  $typeName: string;
  name: string;
  xloc: number;
  yloc: number;
}

const AppToolbar: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { trackEvent, trackUserInteraction } = useTelemetry('Toolbar');
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
  const { currentTheme } = useTheme();

  const activePipeline = useStore((state) =>
    state.pipelines.find((p) => p.id === state.activePipelineId)
  );

  const handleSaveClick = (): void => {
    trackUserInteraction('click', 'save-button', {
      pipelineId: activePipeline?.id,
      nodeCount: activePipeline?.nodes.length || 0,
      edgeCount: activePipeline?.edges.length || 0
    });
    setSaveDialogOpen(true);
  };

  const handleSave = async (filename: string): Promise<void> => {
    if (!activePipeline) return;

    try {
      const pipelineData: PipelineData = {
        $typeName: 'bi.know.protobuf.designer.WorkflowPipeline',
        workflowPipelineType: 0,
        name: filename,
        description: '',
        parameters: [],
        actionTransforms: activePipeline.nodes.map((node) => ({
          $typeName: 'bi.know.protobuf.designer.ActionTransform',
          name: node.data?.label || '',
          description: '',
          type: node.type || '',
        })),
        notepads: [],
        hops: activePipeline.edges.map((edge) => ({
          $typeName: 'bi.know.protobuf.designer.Hop',
          from: activePipeline.nodes.find((n) => n.id === edge.source)?.data?.label,
          to: activePipeline.nodes.find((n) => n.id === edge.target)?.data?.label,
          enabled: !edge.data?.disabled,
          hopType: 0,
        })),
        locations: activePipeline.nodes.map((node) => ({
          $typeName: 'bi.know.protobuf.designer.Location',
          name: node.data?.label || '',
          xloc: Math.round(node.position.x),
          yloc: Math.round(node.position.y),
        })),
      };

      // For development: save to localStorage
      localStorage.setItem(`pipeline_${filename}`, JSON.stringify(pipelineData));

      // TODO: Replace with Spring Boot endpoint call
      // const response = await fetch('/api/pipelines', {
      //     method: 'POST',
      //     headers: { 'Content-Type': 'application/json' },
      //     body: JSON.stringify({ name: filename, data: pipelineData })
      // });

      trackEvent('pipeline-saved', {
        filename,
        pipelineId: activePipeline.id,
        nodeCount: activePipeline.nodes.length,
        edgeCount: activePipeline.edges.length,
        saveMethod: 'localStorage'
      });
      
      message.success(`Pipeline saved as ${filename}`);
      setSaveDialogOpen(false);
    } catch (error) {
      logger.error('Save failed:', error);
      trackEvent('pipeline-save-failed', {
        filename,
        error: error instanceof Error ? error.message : String(error),
        pipelineId: activePipeline?.id,
        nodeCount: activePipeline?.nodes.length || 0
      });
      message.error('Failed to save pipeline');
    }
  };

  const handleMetadataClick = (): void => {
    navigate('/metadata');
  };

  return (
    <>
      {/* <Header style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                padding: '0 16px',
                backgroundColor: '#001529',
                zIndex: 1100
            }}>
                <Space>
                    <Tooltip title="Metadata Manager">
                        <Button
                            type="text"
                            icon={<DatabaseOutlined style={{ color: 'white' }}/>}
                            onClick={handleMetadataClick}
                            style={{ color: 'white' }}
                        />
                    </Tooltip>
                    <Tooltip title="Run Pipeline">
                        <Button
                            type="text"
                            icon={<PlayCircleOutlined style={{ color: 'white' }}/>}
                            onClick={runPipeline}
                            style={{ color: 'white' }}
                        />
                    </Tooltip>
                    <Tooltip title="Preview Data">
                         <Button
                            type="text"
                            icon={<EyeOutlined style={{ color: 'white' }}/>}
                            onClick={previewPipeline}
                            style={{ color: 'white' }}
                        />
                    </Tooltip>
                    <Tooltip title="Stop Execution">
                        <Button
                            type="text"
                            icon={<StopOutlined style={{ color: 'white' }}/>}
                            onClick={stopPipeline}
                            style={{ color: 'white' }}
                        />
                    </Tooltip>
                    <Tooltip title="Save Pipeline">
                        <Button
                            type="text"
                            icon={<SaveOutlined style={{ color: 'white' }}/>}
                            onClick={handleSaveClick}
                            disabled={!activePipeline}
                            style={{ color: 'white' }}
                        />
                    </Tooltip>
                </Space>
            </Header> */}
      <div
        style={{
          padding: '16px',
          paddingLeft: '0px',
          background: 'var(--color-header-background)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 'var(--header-height)',
        }}
      >
        <div
            role={"banner"}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <img
            src={currentTheme.isDark && currentTheme.branding.logoUrlDark 
              ? currentTheme.branding.logoUrlDark 
              : currentTheme.branding.logoUrl || putkiLogo}
            alt={currentTheme.branding.appName}
            style={{
              height: '48px',
              width: 'auto',
              filter: currentTheme.branding.logoUrl.includes('putki-logo.svg') 
                ? 'brightness(0) invert(1)' 
                : 'var(--logo-filter)',
            }}
            onError={(e) => {
              // Fallback to default logo if theme logo fails to load
              e.currentTarget.src = putkiLogo;
              e.currentTarget.style.filter = 'brightness(0) invert(1)';
            }}
          />
          <ProjectDropdown />
          <Tooltip title={t('search.shortcut')} placement="bottom">
            <Button
              type="text"
              icon={<SearchOutlined />}
              onClick={() => {
                // Dispatch a custom event that the App component can listen to
                window.dispatchEvent(new CustomEvent('openGlobalSearch'));
              }}
              style={{ 
                color: 'var(--color-header-text)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: 'var(--border-radius-base)'
              }}
            />
          </Tooltip>
        </div>
        
        {/* Right side - User Profile */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <UserProfile />
        </div>
      </div>
      <SaveDialog
        open={saveDialogOpen}
        onCancel={() => setSaveDialogOpen(false)}
        onSave={handleSave}
        initialName={activePipeline?.name}
      />
    </>
  );
};

export default AppToolbar;
